import { API_BASE_URL, apiRequest, type ApiRequestOptions } from '../http.ts';
import type { APIResponse } from './types/common.ts';
import type { DurableJobSnapshot } from './types/data.ts';
import type {
  BacktestExtensionResponse,
  BacktestExecutionResult,
  BacktestHistoryData,
  BacktestHistoryDetail,
  BacktestRunConfig,
  BacktestSaveResponse,
  HistoryDeleteResponse,
  OptimizationHistoryData,
  OptimizationHistoryDetail,
  OptimizationExecutionResult,
  PortfolioHistoryData,
  PortfolioHistoryDetail,
  PortfolioExecutionResult,
  PortfolioRunConfig,
} from './types/backtest.ts';
import type {
  BacktestProgress,
  BacktestStrategyDefinition,
  BacktestCancelResponse,
  NQMMBacktestUniverse,
} from './types/data.ts';

export interface BacktestOptimizationConfig {
  strategy: string;
  symbol: string;
  start_date: string;
  end_date: string;
  timeframe?: string;
  capital?: number;
  data_source?: string;
  sec_type?: string;
  param_ranges: Record<string, unknown[]>;
  fixed_params?: Record<string, unknown>;
  optimize_for?: string;
  entry_type?: string;
  exit_type?: string;
  backtest_id?: number;
}

export interface ExtendBacktestConfig {
  end_date?: string;
  sec_type?: string;
  refetch?: boolean;
  re_run_oos?: boolean;
  engine?: string;
  isOptimization?: boolean;
}

/** Backtest execution, optimization, and saved-result endpoints. */
export class BacktestsApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(endpoint, options);
  }

  private async submitDurableRun<TResult>(
    endpoint: string,
    config: unknown,
    onQueued?: (jobId: string) => void,
    onProgress?: (snapshot: DurableJobSnapshot) => void,
  ): Promise<TResult> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    headers.set(
      'Idempotency-Key',
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `backtest-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    );
    const response = await this.request<APIResponse<DurableJobSnapshot>>(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(config),
    });
    if (!response.data?.id) throw new Error('Backtest job response did not include a job ID');
    onQueued?.(response.data.id);
    onProgress?.(response.data);

    let snapshot = response.data;
    while (snapshot.status === 'queued' || snapshot.status === 'running' || snapshot.status === 'cancelling') {
      snapshot = await this.getDurableJob(snapshot.id);
      onProgress?.(snapshot);
      if (snapshot.status === 'queued' || snapshot.status === 'running' || snapshot.status === 'cancelling') {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
    if (snapshot.status === 'completed') {
      if (!snapshot.result) throw new Error('Completed backtest job did not include a result');
      return snapshot.result as TResult;
    }
    throw new Error(snapshot.error || snapshot.message || `Backtest job ${snapshot.status}`);
  }

  private async getDurableJob(jobId: string): Promise<DurableJobSnapshot> {
    const response = await this.request<APIResponse<DurableJobSnapshot>>(
      `${this.baseUrl}/backtest/jobs/${encodeURIComponent(jobId)}`,
    );
    if (!response.data) throw new Error('Job status response did not include job data');
    return response.data;
  }

  getStrategies(): Promise<BacktestStrategyDefinition[]> {
    return this.request<BacktestStrategyDefinition[]>(`${this.baseUrl}/backtest/strategies`);
  }

  getNQMMUniverse(): Promise<NQMMBacktestUniverse> {
    return this.request<NQMMBacktestUniverse>(`${this.baseUrl}/backtest/nqmm/universe`);
  }

  getProgress(): Promise<BacktestProgress> {
    return this.request<BacktestProgress>(`${this.baseUrl}/backtest/progress`);
  }

  cancel(): Promise<BacktestCancelResponse> {
    return this.request<BacktestCancelResponse>(`${this.baseUrl}/backtest/cancel`, { method: 'POST' });
  }

  run(
    config: BacktestRunConfig,
    onQueued?: (jobId: string) => void,
    onProgress?: (snapshot: DurableJobSnapshot) => void,
  ): Promise<BacktestExecutionResult> {
    return this.submitDurableRun<BacktestExecutionResult>(`${this.baseUrl}/backtest/run`, config, onQueued, onProgress);
  }

  runPortfolio(config: PortfolioRunConfig, onQueued?: (jobId: string) => void): Promise<PortfolioExecutionResult> {
    return this.submitDurableRun<PortfolioExecutionResult>(`${this.baseUrl}/backtest/portfolio/run`, config, onQueued);
  }

  savePortfolio(result: Record<string, unknown>): Promise<BacktestSaveResponse> {
    return this.request<BacktestSaveResponse>(`${this.baseUrl}/backtest/portfolio/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(result),
    });
  }

  runOptimization(
    config: BacktestOptimizationConfig,
    onQueued?: (jobId: string) => void,
    onProgress?: (snapshot: DurableJobSnapshot) => void,
  ): Promise<OptimizationExecutionResult> {
    return this.submitDurableRun<OptimizationExecutionResult>(`${this.baseUrl}/backtest/optimize`, config, onQueued, onProgress);
  }

  saveBacktest(result: Record<string, unknown>): Promise<BacktestSaveResponse> {
    return this.request<BacktestSaveResponse>(`${this.baseUrl}/backtest/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(result),
    });
  }

  getHistory(
    page: number = 1,
    pageSize: number = 20,
    startDate?: string,
    endDate?: string,
    scope: 'all' | 'mine' | 'shared' = 'all',
  ): Promise<BacktestHistoryData> {
    const params = new URLSearchParams({
      skip: String((page - 1) * pageSize),
      limit: String(pageSize),
    });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (scope !== 'all') params.set('scope', scope);
    return this.request<BacktestHistoryData>(`${this.baseUrl}/backtest/history?${params.toString()}`);
  }

  getDetail(id: number): Promise<BacktestHistoryDetail> {
    return this.request<BacktestHistoryDetail>(`${this.baseUrl}/backtest/history/${id}`);
  }

  getShareUsers(): Promise<Array<{ id: number; username: string }>> {
    return this.request(`${this.baseUrl}/backtest/sharing/users`);
  }

  getSharing(id: number): Promise<{ share_all: boolean; user_ids: number[] }> {
    return this.request(`${this.baseUrl}/backtest/history/${id}/sharing`);
  }

  updateSharing(id: number, shareAll: boolean, userIds: number[]): Promise<{ share_all: boolean; user_ids: number[] }> {
    return this.request(`${this.baseUrl}/backtest/history/${id}/sharing`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ share_all: shareAll, user_ids: userIds }),
    });
  }

  extend(id: number, config: ExtendBacktestConfig = {}): Promise<BacktestExtensionResponse> {
    return this.request<BacktestExtensionResponse>(`${this.baseUrl}/backtest/history/${id}/extend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
  }

  delete(id: number): Promise<HistoryDeleteResponse> {
    return this.request<HistoryDeleteResponse>(`${this.baseUrl}/backtest/history/${id}`, { method: 'DELETE' });
  }

  saveOptimization(result: Record<string, unknown>): Promise<BacktestSaveResponse> {
    return this.request<BacktestSaveResponse>(`${this.baseUrl}/backtest/optimization/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(result),
    });
  }

  getOptimizationHistory(skip: number = 0, limit: number = 20): Promise<OptimizationHistoryData> {
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    return this.request<OptimizationHistoryData>(`${this.baseUrl}/backtest/optimization/history?${params.toString()}`);
  }

  getOptimizationDetail(id: number): Promise<OptimizationHistoryDetail> {
    return this.request<OptimizationHistoryDetail>(`${this.baseUrl}/backtest/optimization/${id}`);
  }

  deleteOptimization(id: number): Promise<HistoryDeleteResponse> {
    return this.request<HistoryDeleteResponse>(`${this.baseUrl}/backtest/optimization/${id}`, { method: 'DELETE' });
  }

  getPortfolioHistory(skip: number = 0, limit: number = 100): Promise<PortfolioHistoryData> {
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    return this.request<PortfolioHistoryData>(`${this.baseUrl}/backtest/portfolio/history?${params.toString()}`);
  }

  deletePortfolio(id: number): Promise<HistoryDeleteResponse> {
    return this.request<HistoryDeleteResponse>(`${this.baseUrl}/backtest/portfolio/${id}`, { method: 'DELETE' });
  }

  getPortfolioById(id: number): Promise<PortfolioHistoryDetail> {
    return this.request<PortfolioHistoryDetail>(`${this.baseUrl}/backtest/portfolio/${id}`);
  }
}
